import React from 'react';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  ExternalLoginDocument,
  ExternalLoginMutation,
  IdpLoginDocument,
  IdpLoginMutation,
  IssueChallengeDocument,
  IssueChallengeMutation,
  MfaLoginDocument,
  MfaLoginMutation,
  PasswordResetDocument,
  PasswordResetQuery,
  SessionLoginDocument,
  SessionLoginMutation
} from '@cvent/universal-login-web-graphql/operations';
import { Status } from '@cvent/universal-login-web-graphql/types';

import { getProductInfo, getSsoRedirect, handleCustomCookies } from '@components/helpers/PreRenderHelpers';
import { SessionHandler } from '@components/helpers/SessionHandler';
import { titleVar } from '@components/framework/ReactiveVars';
import { AlertProps, ErrorProvider } from '@components/providers/ErrorProvider';
import { FormDisplayProvider } from '@components/providers/FormDisplayProvider';
import { ProductProvider } from '@components/providers/ProductProvider';
import { ErrorHandler } from '@components/errors/ErrorHandler';
import { LogoContainer } from '@components/containers/LogoContainer';
import { CardContainer } from '@components/containers/CardContainer';
import { FooterContainer } from '@components/containers/FooterContainer';
import { ForgotPasswordForm } from '@components/forms/ForgotPasswordForm';
import { MfaForm } from '@components/forms/MfaForm';
import { SsoLoginForm } from '@components/forms/SsoLoginForm';
import { ExternalCventLoginForm } from '@components/forms/ExternalCventLoginForm';

export const getServerSideProps: GetServerSideProps = async ctx => {
  const query = ctx.query;
  const res = ctx.res;
  const targetProductId = process.env.MY_CVENT_PRODUCT_ID;

  let softError: AlertProps;

  const productInfo = await getProductInfo(ctx, targetProductId);
  res.statusCode = productInfo.statusCode;

  if (productInfo.statusCode !== 200) {
    return {
      props: {
        error: { ...productInfo }
      }
    };
  }

  if ('identityProviderName' in query) {
    const ssoRedirect = await getSsoRedirect(ctx);

    if (ssoRedirect.statusCode === 200) {
      if (!ssoRedirect.transferState) {
        softError = {
          type: 'danger',
          message: ssoRedirect?.contentKey
        };
      } else if (ssoRedirect.transferState && ssoRedirect.transferState.status === Status.Success) {
        return {
          redirect: {
            destination: `/sign-on?transferId=${ssoRedirect.transferState.transferId}`,
            permanent: false
          }
        };
      }
    }
  }

  const { logout } = handleCustomCookies(ctx);

  return {
    props: {
      codeChallenge: query?.codeChallenge ?? null,
      codeChallengeMethod: query?.codeChallengeMethod ?? null,
      logout,
      nonce: query?.nonce ?? null,
      redirectUri: query?.redirectUri ?? null,
      scope: query?.scope ?? null,
      softError: softError ?? null,
      sourceProduct: productInfo.product,
      state: query?.state ?? null,
      targetProduct: productInfo.product
    }
  };
};

const TEST_ID = 'product-login';
export const LoginPage = ({
  codeChallenge,
  codeChallengeMethod,
  logout,
  nonce,
  redirectUri,
  scope,
  softError,
  sourceProduct,
  state,
  targetProduct,
  error
}: InferGetServerSidePropsType<typeof getServerSideProps>): JSX.Element => {
  titleVar(sourceProduct?.label);

  const [sessionLogin] = useMutation<SessionLoginMutation>(SessionLoginDocument, {
    variables: {
      codeChallenge,
      codeChallengeMethod,
      nonce,
      redirectUri,
      state,
      scope,
      targetProductId: targetProduct?.id
    }
  });
  const [externalLogin] = useMutation<ExternalLoginMutation>(ExternalLoginDocument, {
    variables: {
      codeChallenge,
      codeChallengeMethod,
      nonce,
      redirectUri,
      scope,
      state,
      targetProductId: targetProduct?.id
    }
  });
  const [idpLogin] = useMutation<IdpLoginMutation>(IdpLoginDocument, {
    variables: {
      codeChallenge,
      codeChallengeMethod,
      nonce,
      redirectUri,
      state,
      scope,
      targetProductId: targetProduct?.id
    }
  });
  const [mfaLogin] = useMutation<MfaLoginMutation>(MfaLoginDocument);
  const [mfaChallenge] = useMutation<IssueChallengeMutation>(IssueChallengeDocument);
  const [resetPassword] = useLazyQuery<PasswordResetQuery>(PasswordResetDocument);

  return (
    <ErrorProvider values={{ alert: softError, error }}>
      <ErrorHandler>
        <ProductProvider values={{ sourceProduct, targetProduct }}>
          <FormDisplayProvider values={{}}>
            <SessionHandler testID={TEST_ID} redirectMutation={sessionLogin} logout={logout}>
              <LogoContainer testID={TEST_ID} />
              <CardContainer testID={TEST_ID}>
                <>
                  <ExternalCventLoginForm testID={TEST_ID} redirectMutation={externalLogin} />
                  <MfaForm testID={TEST_ID} redirectMutation={mfaLogin} usersMutation={mfaChallenge} />
                  <ForgotPasswordForm testID={TEST_ID} redirectQuery={resetPassword} />
                  <SsoLoginForm testID={TEST_ID} redirectMutation={idpLogin} />
                </>
              </CardContainer>
              <FooterContainer testID={TEST_ID} />
            </SessionHandler>
          </FormDisplayProvider>
        </ProductProvider>
      </ErrorHandler>
    </ErrorProvider>
  );
};
export default LoginPage;
